(function () {
  const SESSION_STORAGE_KEY = "VELOCITY_MIXPANEL_TRACKING_ID";
  const PARTNER_BRANDS_SECTION_ID = "collections_grid_Yxkrk6";
  const EVENT_PREFIX = "velocity_marketplace_";
  const CLICK_SUFFIX = "_clicked";
  const PAGE_VIEW_SUFFIX = "_viewed";
  const GA4_MAX_EVENT_NAME_LENGTH = 40;

  function shouldSkipTracking() {
    return window.Shopify && window.Shopify.designMode;
  }

  function getSessionId() {
    let velocityTrackingId = sessionStorage.getItem(SESSION_STORAGE_KEY);

    if (!velocityTrackingId) {
      velocityTrackingId = crypto.randomUUID();
      sessionStorage.setItem(SESSION_STORAGE_KEY, velocityTrackingId);
    }

    return velocityTrackingId;
  }

  function getPageContext() {
    return window.velocityMixpanelContext || {};
  }

  function buildEventName(eventName, suffix) {
    const baseName = eventName.startsWith(EVENT_PREFIX)
      ? eventName.slice(EVENT_PREFIX.length)
      : eventName;

    return `${EVENT_PREFIX}${baseName}${suffix || ""}`;
  }

  function getGoogleEventName(eventName) {
    return eventName.startsWith(EVENT_PREFIX)
      ? eventName.slice(EVENT_PREFIX.length)
      : eventName;
  }

  function getAnalyticsProperties(properties) {
    return {
      mixpanel_session_id: getSessionId(),
      ...(properties || {}),
    };
  }

  function trackMixpanel(eventName, properties) {
    if (typeof mixpanel === "undefined") {
      return;
    }

    mixpanel.track(eventName, getAnalyticsProperties(properties));
  }

  function trackGoogle(eventName, properties) {
    if (typeof gtag !== "function") {
      return;
    }

    const analyticsProperties = getAnalyticsProperties(properties);
    const googleEventName = getGoogleEventName(eventName);

    if (googleEventName.length <= GA4_MAX_EVENT_NAME_LENGTH) {
      gtag("event", googleEventName, analyticsProperties);
      return;
    }

    gtag("event", "custom_event", {
      ...analyticsProperties,
      event_name: googleEventName,
    });
  }

  function dispatchAnalytics(eventName, properties) {
    if (shouldSkipTracking()) {
      return;
    }

    trackMixpanel(eventName, properties);
    trackGoogle(eventName, properties);
  }

  function track(eventName, properties) {
    dispatchAnalytics(buildEventName(eventName), properties);
  }

  function trackClick(eventName, properties) {
    dispatchAnalytics(buildEventName(eventName, CLICK_SUFFIX), properties);
  }

  function trackPageViewEvent(eventName, properties) {
    dispatchAnalytics(buildEventName(eventName, PAGE_VIEW_SUFFIX), properties);
  }

  function normalizeText(value) {
    return (value || "").trim().replace(/\s+/g, " ").slice(0, 120);
  }

  function resolvePageViewEvent() {
    const ctx = getPageContext();
    const path = window.location.pathname;

    if (path.includes("/account/profile")) {
      return "profile_page";
    }

    if (/\/account\/orders\/[^/]+/.test(path)) {
      return "order_details_page";
    }

    if (path.includes("/account/orders")) {
      return "orders_page";
    }

    switch (ctx.page_type) {
      case "index":
        return "home_page";
      case "product":
        return "product_page";
      case "cart":
        return "cart_page";
      default:
        break;
    }

    if (ctx.template === "login" || path.includes("/account/login")) {
      return "login_page";
    }

    if (ctx.template === "order") {
      return "order_details_page";
    }

    if (ctx.template === "account") {
      return "orders_page";
    }

    return null;
  }

  function getPageViewProperties(eventName) {
    const ctx = getPageContext();
    const properties = {};

    if (eventName === "product_page" && ctx.product_handle) {
      properties.product_handle = ctx.product_handle;
    }

    return properties;
  }

  function trackPageView() {
    const eventName = resolvePageViewEvent();

    if (!eventName) {
      return;
    }

    trackPageViewEvent(eventName, getPageViewProperties(eventName));
  }

  function getSidebarFilterType(element) {
    const card = element.closest(".emi-sidebar__card");

    if (!card) {
      return null;
    }

    const title = card.querySelector(".emi-sidebar__title")?.textContent?.trim();

    if (title === "DOWNPAYMENT") {
      return "downpayment_filter";
    }

    if (title === "CATEGORIES") {
      return "category_filter";
    }

    return null;
  }

  function resolveClickEvent(target) {
    if (!(target instanceof Element)) {
      return null;
    }

    if (target.closest("cart-drawer .drawer__close")) {
      return {
        event: "close_cart_drawer",
      };
    }

    if (target.closest(".product-form__submit, product-form [type='submit']")) {
      return {
        event: "add_to_cart",
      };
    }

    if (target.closest("cart-remove-button, [id^='CartDrawer-Remove']")) {
      return {
        event: "remove_item",
      };
    }

    if (target.closest(".quantity__button[name='plus']")) {
      return {
        event: "add_item",
      };
    }

    if (target.closest(".quantity__button[name='minus']")) {
      return {
        event: "remove_item",
      };
    }

    const emiTenure = target.closest(".emi-calculator .emi-tenure");

    if (emiTenure) {
      return {
        event: "emi_works_option",
        properties: {
          months: emiTenure.dataset.months,
        },
      };
    }

    if (target.closest(".emi-sidebar__chip")) {
      const chip = target.closest(".emi-sidebar__chip");

      return {
        event: "emi_filter",
        properties: {
          label: normalizeText(chip.textContent),
          href: chip.getAttribute("href"),
        },
      };
    }

    const sidebarLink = target.closest(".emi-sidebar__list-link");

    if (sidebarLink) {
      const filterType = getSidebarFilterType(sidebarLink);

      if (filterType) {
        return {
          event: filterType,
          properties: {
            label: normalizeText(
              sidebarLink.querySelector("span:first-child")?.textContent
            ),
            href: sidebarLink.getAttribute("href"),
          },
        };
      }
    }

    if (target.closest(".emi-promo-card")) {
      const card = target.closest(".emi-promo-card");

      return {
        event: "promo_card",
        properties: {
          label: normalizeText(
            card.querySelector(".emi-promo-card__heading")?.textContent
          ),
          href: card.getAttribute("href"),
        },
      };
    }

    const partnerBrandSection = target.closest(
      `[id$='${PARTNER_BRANDS_SECTION_ID}']`
    );

    if (partnerBrandSection && target.closest(".collections__card a")) {
      const card = target.closest(".collections__card");

      return {
        event: "partner_brand",
        properties: {
          label: normalizeText(card?.textContent),
          href: target.closest("a")?.getAttribute("href"),
        },
      };
    }

    const productCard = target.closest(".product-card, .product-card-big");

    if (
      productCard &&
      !target.closest(
        ".js-color-swatches, .quick-add, .product-form__controls, button[type='submit']"
      )
    ) {
      return {
        event: "product_card",
        properties: {
          product_handle: normalizeText(productCard.dataset.product),
          href: target.closest("a")?.getAttribute("href"),
        },
      };
    }

    return null;
  }

  function handleClick(event) {
    const clickEvent = resolveClickEvent(event.target);

    if (!clickEvent) {
      return;
    }

    trackClick(clickEvent.event, clickEvent.properties);
  }

  function handleVariantSelectChange(event) {
    const target = event.target;

    if (!(target instanceof HTMLElement)) {
      return;
    }

    if (
      target.matches(
        ".product-form__input input[type='radio'], .js-color-swatches input[type='radio']"
      )
    ) {
      trackClick("size_color_change", {
        option_value: target.value,
        option_name: target.name,
      });
      return;
    }

    if (
      target instanceof HTMLSelectElement &&
      target.closest(".product-form, variant-selects, .product-form__input")
    ) {
      trackClick("size_color_change", {
        option_value: target.value,
        option_name: target.name,
      });
    }
  }

  function initCartDrawerTracking() {
    const drawer = document.querySelector("cart-drawer");

    if (!drawer) {
      return;
    }

    let isOpen = drawer.classList.contains("active");

    const observer = new MutationObserver(() => {
      const isNowOpen = drawer.classList.contains("active");

      if (isNowOpen && !isOpen) {
        trackPageViewEvent("cart_drawer");
      }

      isOpen = isNowOpen;
    });

    observer.observe(drawer, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  function initAnalytics() {
    if (shouldSkipTracking()) {
      return;
    }

    const velocityTrackingId = getSessionId();
    const isNewSession = !sessionStorage.getItem(
      `${SESSION_STORAGE_KEY}_INITIALIZED`
    );

    if (typeof mixpanel !== "undefined") {
      mixpanel.register({
        mixpanel_session_id: velocityTrackingId,
      });
    }

    if (typeof gtag === "function") {
      gtag("set", "user_properties", {
        mixpanel_session_id: velocityTrackingId,
      });
    }

    if (isNewSession) {
      track("initialize_session");
      sessionStorage.setItem(`${SESSION_STORAGE_KEY}_INITIALIZED`, "1");
    }

    trackPageView();
    initCartDrawerTracking();

    document.addEventListener("click", handleClick, true);
    document.addEventListener("change", handleVariantSelectChange, true);
  }

  document.addEventListener("DOMContentLoaded", initAnalytics);
})();
